import React, { useMemo } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { useS3Service } from '../../hooks/useS3Service';
import { useS3Filter } from '../../contexts/S3FilterContext';

/**
 * Month option type
 */
interface MonthOption {
  label: string;
  value: string;
}

/**
 * Full month names for display (capitalized as they appear in S3)
 */
const MONTH_NAMES: Record<string, string> = {
  January: 'January',
  February: 'February',
  March: 'March',
  April: 'April',
  May: 'May',
  June: 'June',
  July: 'July',
  August: 'August',
  September: 'September',
  October: 'October',
  November: 'November',
  December: 'December',
};

/**
 * Month names as capitalized keys for folder matching
 */
const ALL_MONTH_KEYS = Object.keys(MONTH_NAMES);

/**
 * Helper: Check if a folder name is a valid month (case-insensitive)
 */
const isValidMonth = (folderName: string): string | null => {
  // Try exact match first
  if (MONTH_NAMES[folderName]) {
    return folderName;
  }
  
  // Try case-insensitive match
  const lowerName = folderName.toLowerCase();
  const match = ALL_MONTH_KEYS.find(key => key.toLowerCase() === lowerName);
  return match || null;
};

/**
 * Props for MonthFilterComponent
 */
export interface MonthFilterComponentProps {
  disabled?: boolean;
  loading?: boolean;
  yearFilter?: string | null;
  basePath?: string;
}

/**
 * MonthFilterComponent - Multi-select filter for S3 file months
 *
 * Displays available months for the selected year from S3 folder structure.
 * If no year is selected, all available months are shown.
 */
export const MonthFilterComponent: React.FC<MonthFilterComponentProps> = ({
  disabled = false,
  loading = false,
  yearFilter,
  basePath = '',
}) => {
  const { filters, setSelectedMonths } = useS3Filter();
  const { s3Service, isAuthenticated } = useS3Service();
  const [months, setMonths] = React.useState<string[]>([]);
  const [loadingMonths, setLoadingMonths] = React.useState(false);

  /**
   * Extract available months from S3 folder structure
   * Queries from: basePath + partner/ + year/ to find month folders
   */
  React.useEffect(() => {
    const extractMonths = async () => {
      try {
        setLoadingMonths(true);
        if (!s3Service || !isAuthenticated) {
          setMonths([]);
          return;
        }

        // Build query path: basePath + partner/ (if selected) + year/ (if selected)
        let queryPath = basePath;
        if (filters.selectedPartners.length === 1) {
          queryPath += `${filters.selectedPartners[0]}/`;
        }
        if (yearFilter) {
          queryPath += `${yearFilter}/`;
        }
        
        console.log('[MonthFilter] Querying path:', queryPath);
        const result = await s3Service.listObjects(queryPath);

        // Extract unique month names from folders
        const monthSet = new Set<string>();

        // Filter for folders only
        const folders = result.items.filter((item) => item.type === 'folder');
        
        console.log('[MonthFilter] Found folders:', folders.length);
        
        folders.forEach((folder) => {
          // Extract folder name from key (remove queryPath and trailing slash)
          let folderName = folder.key;
          if (queryPath && folderName.startsWith(queryPath)) {
            folderName = folderName.substring(queryPath.length);
          }
          folderName = folderName.replace(/\/$/, '').split('/')[0];
          
          console.log('[MonthFilter] Checking folder:', folder.key, '-> folderName:', folderName);
          
          // Check if folder name is a valid month (case-insensitive)
          const matchedMonth = isValidMonth(folderName);
          if (matchedMonth) {
            console.log('[MonthFilter] Adding month:', matchedMonth);
            monthSet.add(matchedMonth); // Store the capitalized version
          }
        });

        // Sort months by their calendar order (preserving capitalization)
        const sortedMonths = ALL_MONTH_KEYS.filter((month) => monthSet.has(month));
        console.log('[MonthFilter] Final months:', sortedMonths);
        setMonths(sortedMonths);
      } catch (error) {
        console.warn('Failed to extract months from S3:', error);
        setMonths([]);
      } finally {
        setLoadingMonths(false);
      }
    };

    extractMonths();
  }, [s3Service, isAuthenticated, yearFilter, basePath, filters.selectedPartners]);

  /**
   * Create month options for display
   */
  const monthOptions = useMemo<MonthOption[]>(
    () =>
      months.map((month) => ({
        label: MONTH_NAMES[month] || month,
        value: month,
      })),
    [months]
  );

  /**
   * Get currently selected month values
   */
  const selectedValues = useMemo<MonthOption[]>(
    () =>
      monthOptions.filter((option) =>
        filters.selectedMonths.includes(option.value)
      ),
    [filters.selectedMonths, monthOptions]
  );

  return (
    <Autocomplete
      multiple
      options={monthOptions}
      getOptionLabel={(option) => option.label}
      value={selectedValues}
      onChange={(_, newValues) => {
        setSelectedMonths(newValues.map((v) => v.value));
      }}
      disabled={disabled || loadingMonths || loading || !yearFilter}
      loading={loadingMonths || loading}
      fullWidth
      noOptionsText={yearFilter ? 'No months found for selected year' : 'Select a year first'}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Months"
          placeholder={yearFilter ? 'Select months...' : 'Select a year first'}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {(loadingMonths || loading) && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default MonthFilterComponent;
